/**
 * Determines whether a string starts with any prefix in a given array of prefixes
 *
 * @param {string} s The string to check
 * @param {string[]} prefixes The prefixes to check against the string
 * @returns {boolean} True if the string being checked starts with any supplied prefix
 */
function startsWithAny(s: string, prefixes: string[]): boolean {
	if (!s) {
		return false;
	}

	for (const prefix of prefixes) {
		if (s.startsWith(prefix)) {
			return true;
		}
	}

	return false;
}

/** List of recognized schemes for uri's. If you want to allow users to enter additional schemes such as ftp enter them here. */
const supportedSchemes = ['http://', 'https://'];

/**
 * Returns true if the passed in URI represents an absolute URL with a supported scheme.
 *
 * @param uri The URI to check
 */
export function IsAbsoluteUrl(uri: string): boolean {
	return startsWithAny(uri, supportedSchemes);
}

/**
 * Base URL Formatting implementation.
 * @param value The string that needs to be formatted into a friendly URL.
 * @param keepTrailingSlash Determines whether a trailing slash should be kept. eg: /test/?a=123 vs /test?a=234
 */
function FormatUrlInternal(value: string, keepTrailingSlash = false, allowAnchors = false): string {
	if (!value) {
		return '';
	}

	if (value === '/') {
		return value;
	}

	if (IsAbsoluteUrl(value)) {
		return value;
	}

	const a = 'àáäâãåèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ·_,:;';
	const b = 'aaaaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh-----';
	const p = new RegExp(a.split('').join('|'), 'g');

	let cleanedUrl = value.toString();

	// preserve extension and query, for eg.: '/store/item.aspx?Id=10'
	const extensionAndQueryRegExp = allowAnchors
		? new RegExp(/(\.[^\/]+$)|(\.[^\/]+\?.*$)|(\?.*$)|(\#.*$)/)
		: new RegExp(/(\.[^\/]+$)|(\.[^\/]+\?.*$)|(\?.*$)/);
	const match = extensionAndQueryRegExp.exec(cleanedUrl);
	const isMatch = match && match.length ? true : false;
	const extensionAndQuery = (match && match.length && match[0]) || '';

	if (isMatch) {
		cleanedUrl = cleanedUrl.replace(extensionAndQuery, '');
	}

	cleanedUrl = cleanedUrl
		.toLowerCase()
		.replace(/\s+/g, '-') // Replace spaces with -
		.replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
		.replace(/&/g, '-and-') // Replace & with 'and'
		.replace(/[^\w\-\/]+/g, '') // Remove all non-word characters
		.replace(/\-\-+/g, '-') // Replace multiple - with single -
		.replace(/\/\/+/g, '/') // Replace multiple - with single -
		.replace(/^-+/, '') // Trim - from start of text
		.replace(/-+$/, '');

	if (!cleanedUrl.startsWith('/')) {
		cleanedUrl = '/' + cleanedUrl;
	}

	if (!keepTrailingSlash && cleanedUrl.endsWith('/')) {
		cleanedUrl = cleanedUrl.slice(0, -1);
	}

	if (isMatch) {
		cleanedUrl = cleanedUrl + extensionAndQuery;
	}

	return cleanedUrl;
}

/**
 * Converts a string into a url-friendly string.
 *
 * @param value The value to convert and/or format into a URL
 */
export function FormatUrl(value: string): string {
	return FormatUrlInternal(value);
}

/**
 * Formats the URL but keeps the trailing slash. This is useful when dealing with 301 redirects in particular.
 * @param value
 */
export function FormatUrlKeepTrailingSlash(value: string) {
	return FormatUrlInternal(value, true);
}

/**
 * Formats the URL but allows anchors. This is useful when dealing creating menu items.
 * @param value
 */
export function FormatUrlAllowAnchors(value: string) {
	return FormatUrlInternal(value, false, true);
}
